/* eslint-disable no-plusplus */
import React, { FC, useCallback } from 'react';
import StyledBox from 'views/rebranded-module/GeoViewV2/components/MapSection/MapSection.styles';
import { Box, Button, IconButton } from '@mui/material';
import { Map, LoadingDots } from 'views/common/components';
import useMapLogic from 'views/rebranded-module/GeoViewV2/hooks/useMapLogic';
import dynamic from 'next/dynamic';
import CloseIcon from '@mui/icons-material/Close';
import { RiLoopLeftLine } from 'react-icons/ri';
import { useContent } from 'views/rebranded-module/common/context/useContent';

const MapInfoCardSection = dynamic(() => import('views/rebranded-module/GeoViewV2/components/MapInfoCardSection').then(
  (file) => file.MapInfoCardSection,
));

const MapSection: FC<any> = (props) => {
  const {
    mapLocations,
    selectedPlace,
    handleBikeCardClick,
    isWigetOpen,
    handleMapChange,
    hoveredBike,
    renderSkeleton,
    handleClose,
    currentFilterParams,
    // "Search this area" props (from GeoViewV2):
    // unsearchedMapBounds: non-null when user has panned/zoomed — shows the button
    // onSearchThisArea: callback that triggers the actual API fetch
    // isMapLoading: true while the fetch is in-flight — shows overlay, disables map
    unsearchedMapBounds,
    onSearchThisArea,
    isMapLoading,
    handleMapLoadingFlag,
  } = props;

  const cmsData = useContent('GeoPage');

  const {
    mapCardData,
    renderMapMarkers,
    handleGoogleMapLoaded,
    isTablet,
    handleMapCardClose,
    fittingBounds,
  } = useMapLogic({
    mapLocations,
    selectedPlace,
    hoveredBike,
    currentFilterParams,
    isMapLoading,
    handleMapLoadingFlag,
  });

  const handleMapOnChange = useCallback((...args) => {
    // Block map onChange events while loading to prevent storing new
    // pending bounds during an in-flight request. Also guard against
    // programmatic fitBounds changes (!fittingBounds).
    if (isMapLoading) return;
    if (!fittingBounds) {
      handleMapChange(...args);
    }
  }, [isMapLoading, fittingBounds, handleMapChange]);

  return (
    <StyledBox id="geoMap">
      <IconButton onClick={handleClose} className="MSCloseBtn showTABLET">
        <CloseIcon />
      </IconButton>
      {renderSkeleton && (
        <Box className="MSLoadingDotsInnerContainer">
          <LoadingDots />
        </Box>
      )}
      {
        /* "Search this area" floating pill button — only visible when the user
        has panned/zoomed (unsearchedMapBounds !== null) and no fetch is in progress.
        Positioned center-top via MSSearchThisAreaBtn styles. */
        ((!isMapLoading) && unsearchedMapBounds) && (
          <Button
            variant="contained"
            className="MSSearchThisAreaBtn"
            onClick={onSearchThisArea}
            startIcon={<RiLoopLeftLine />}
          >
            {cmsData?.searchThisAreaLabel}
          </Button>
        )
      }
      <Map
        darkMode
        defaultCenter={{
          lat: 16.213487,
          lng: -118.759664,
        }}
        defaultZoom={6}
        handleMapOnChange={handleMapOnChange}
        // Physically disable map dragging via google-map-react's draggable prop
        // during loading, providing tactile feedback that the map is locked.
        draggable={!isMapLoading}
        renderMapMarkers={renderMapMarkers}
        onGoogleApiLoaded={({ map }) => handleGoogleMapLoaded(map)}
      />
      {mapCardData.open && !isWigetOpen && (
        <MapInfoCardSection
          handleBikeCardClick={handleBikeCardClick}
          open={mapCardData.open}
          handleClose={handleMapCardClose}
          rentalsItems={mapCardData.rentalsItems}
          loading={mapCardData.loading}
          containerId={isTablet ? 'geoPage' : 'geoMap'}
        />
      )}
    </StyledBox>
  );
};

export { MapSection };
